/**
 * Intake caps for the Batch queue (spec §4.2.8 plus memory-safety limits):
 * file count, per-file bytes, and total bytes per run. Enforced when files are
 * added — every rejection produces a visible notice, never a silent drop. Size
 * checks use `File.size` so oversized files are never read into memory.
 */

/** Spec §4.2.8: up to 200 files per batch. */
export const MAX_FILES = 200;
/** Per-file byte cap — a subtitle file larger than this is almost certainly not one. */
export const MAX_FILE_BYTES = 20 * 1024 * 1024; // 20 MiB
/** Total byte cap per run, bounding worker/ZIP peak memory. */
export const MAX_TOTAL_BYTES = 200 * 1024 * 1024; // 200 MiB

const MIB = 1024 * 1024;

/** The minimal shape of `File` the caps need (keeps tests framework-free). */
export interface SizedFile {
  name: string;
  size: number;
}

/**
 * Apply the caps to a picked file list, returning the accepted files plus a
 * human-readable notice per rejection.
 */
export function applyCaps<F extends SizedFile>(picked: F[]): { accepted: F[]; notices: string[] } {
  const notices: string[] = [];
  if (picked.length > MAX_FILES) {
    notices.push(`Only the first ${MAX_FILES} files are processed per batch.`);
  }
  const accepted: F[] = [];
  let total = 0;
  for (const f of picked.slice(0, MAX_FILES)) {
    if (f.size > MAX_FILE_BYTES) {
      notices.push(
        `${f.name} skipped: larger than the ${MAX_FILE_BYTES / MIB} MiB per-file limit.`,
      );
      continue;
    }
    if (total + f.size > MAX_TOTAL_BYTES) {
      notices.push(
        `${f.name} skipped: batch would exceed the ${MAX_TOTAL_BYTES / MIB} MiB total limit.`,
      );
      continue;
    }
    total += f.size;
    accepted.push(f);
  }
  return { accepted, notices };
}
